import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../context/AppContext';

function MoonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );
}

function SunIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/>
      <line x1="12" y1="1" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  );
}

function EnvSwitcher() {
  const { envId, setEnvId, customUrl, setCustomUrl, currentEnv, environments } = useApp();
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [editingCustom, setEditingCustom] = useState(false);
  const [draft, setDraft] = useState(customUrl);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
  const ref = useRef(null);
  const panelRef = useRef(null);
  const closeTimer = useRef(null);
  const openRef = useRef(false);
  const closingRef = useRef(false);

  openRef.current = open;
  closingRef.current = closing;

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const close = () => {
    if (!openRef.current || closingRef.current) return;
    if (!isMobile) {
      setOpen(false);
      setEditingCustom(false);
      return;
    }
    setClosing(true);
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      setOpen(false);
      setClosing(false);
      setEditingCustom(false);
    }, 400);
  };

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      const inTrigger = ref.current?.contains(e.target);
      const inPanel = panelRef.current?.contains(e.target);
      if (!inTrigger && !inPanel) close();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, isMobile]);

  const select = (id) => {
    setEnvId(id);
    if (id === 'custom') setEditingCustom(true);
    else close();
  };

  const saveCustom = () => {
    setCustomUrl(draft);
    setEditingCustom(false);
    close();
  };

  const panel = (open || closing) && (
    <>
      <div
        className={`app-header__env-backdrop${closing ? ' is-closing' : ''}`}
        onClick={close}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        className={`app-header__env-dropdown${closing ? ' is-closing' : ''}`}
        role="listbox"
      >
        <div className="app-header__env-dropdown-title">
          <span>API Environment</span>
          <button
            type="button"
            className="app-header__env-close"
            onClick={close}
            aria-label="Close environment menu"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        {(environments || []).map(env => (
          <div
            key={env.id}
            onClick={() => select(env.id)}
            className="app-header__env-option"
            data-active={envId === env.id || undefined}
            role="option"
            aria-selected={envId === env.id}
          >
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: env.color, flexShrink: 0, boxShadow: envId === env.id ? `0 0 6px ${env.color}` : 'none' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: envId === env.id ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                {env.label}
              </div>
              {env.id !== 'custom' ? (
                <div className="app-header__env-url">{env.url}</div>
              ) : (
                <div className="app-header__env-url">{customUrl || 'Click to set URL'}</div>
              )}
            </div>
            {envId === env.id && (
              <svg width="12" height="12" viewBox="0 0 20 20" fill={env.color}><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
            )}
          </div>
        ))}
        {(envId === 'custom' || editingCustom) && (
          <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-base)' }}>
            <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginBottom: 6, fontWeight: 600 }}>Custom base URL</div>
            <div style={{ display: 'flex', gap: 7 }}>
              <input
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && saveCustom()}
                placeholder="https://your-api.com"
                autoFocus
                style={{
                  flex: 1, minWidth: 0, padding: '6px 10px',
                  border: '1px solid var(--accent)', borderRadius: 6,
                  background: 'var(--bg-elevated)', color: 'var(--text-primary)',
                  fontSize: 12, fontFamily: 'JetBrains Mono, monospace', outline: 'none',
                  boxShadow: '0 0 0 2px var(--accent-glow)',
                }}
              />
              <button type="button" onClick={saveCustom} style={{
                padding: '6px 12px', borderRadius: 6, border: 'none',
                background: 'var(--accent)', color: '#fff',
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}>Set</button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div ref={ref} className="app-header__env">
      <button
        type="button"
        onClick={() => {
          if (open || closing) close();
          else setOpen(true);
        }}
        title={`Environment: ${currentEnv.label}`}
        className="app-header__env-btn"
        aria-expanded={open && !closing}
        aria-haspopup="listbox"
      >
        <div
          className="app-header__env-dot"
          style={{ background: currentEnv.color, boxShadow: `0 0 6px ${currentEnv.color}` }}
        />
        <span className="app-header__env-label">{currentEnv.label}</span>
        <svg width="10" height="10" viewBox="0 0 20 20" fill="currentColor" className="app-header__env-chevron" style={{ transform: open && !closing ? 'rotate(180deg)' : 'none' }}>
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>

      {panel && (isMobile ? createPortal(panel, document.body) : panel)}
    </div>
  );
}

function TokenBadge() {
  const { savedToken, setSavedToken } = useApp();
  if (!savedToken) return null;

  const short = savedToken.length > 18 ? savedToken.slice(0, 9) + '…' + savedToken.slice(-5) : savedToken;

  return (
    <div className="app-header__token" title={`JWT saved: ${short}`}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
      </svg>
      <span className="app-header__token-text">{short}</span>
      <button
        type="button"
        onClick={() => setSavedToken('')}
        title="Clear saved token"
        className="app-header__token-clear"
        aria-label="Clear saved token"
      >
        <svg width="10" height="10" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  );
}

function SearchResults({ results, onSelect, emptyQuery }) {
  if (!emptyQuery) {
    return (
      <div className="app-header__search-empty">
        Type to search endpoints and docs
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="app-header__search-empty">
        No results found
      </div>
    );
  }

  return (
    <div className="app-header__search-results-list">
      {results.map((r) => (
        <button
          key={r.id}
          type="button"
          onClick={() => onSelect(r.id)}
          className="app-header__search-result"
        >
          <span className="app-header__search-result-icon">{r.icon}</span>
          <div className="app-header__search-result-body">
            <div className="app-header__search-result-title">{r.name}</div>
            {r.endpoint && <div className="app-header__search-result-endpoint">{r.endpoint}</div>}
          </div>
        </button>
      ))}
    </div>
  );
}

function SearchBar({ search, setSearch, searchOpen, setSearchOpen, results, onSelect, inputRef }) {
  return (
    <div className="app-header__search app-header__search--inline">
      <div className={`app-header__search-box${searchOpen ? ' is-focused' : ''}`}>
        <SearchIcon />
        <input
          ref={inputRef}
          value={search}
          onChange={e => setSearch(e.target.value)}
          onFocus={() => setSearchOpen(true)}
          onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
          placeholder="Search endpoints..."
          aria-label="Search endpoints"
        />
        <kbd className="app-header__search-kbd">⌘K</kbd>
      </div>

      {searchOpen && search && results.length > 0 && (
        <div className="app-header__search-dropdown">
          <SearchResults results={results} onSelect={onSelect} emptyQuery={Boolean(search.trim())} />
        </div>
      )}
    </div>
  );
}

function MobileSearchOverlay({ search, setSearch, results, onSelect, onClose, inputRef }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const timer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => {
      document.body.style.overflow = '';
      clearTimeout(timer);
    };
  }, [inputRef]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="app-header__search-overlay" role="dialog" aria-modal="true" aria-label="Search">
      <div className="app-header__search-overlay-bar">
        <div className="app-header__search-box is-focused">
          <SearchIcon />
          <input
            ref={inputRef}
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search endpoints..."
            aria-label="Search endpoints"
          />
        </div>
        <button type="button" className="app-header__search-cancel" onClick={onClose}>
          Cancel
        </button>
      </div>
      <SearchResults results={results} onSelect={onSelect} emptyQuery={Boolean(search.trim())} />
    </div>
  );
}

export default function Header({
  sidebarOpen, setSidebarOpen, theme, toggleTheme, setActivePage,
  product, apis = [], onBackToProducts,
}) {
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const desktopSearchRef = useRef(null);
  const mobileSearchRef = useRef(null);

  const allPages = [
    { id: 'home', name: 'Getting Started', icon: '🚀' },
    { id: 'status-codes', name: 'Status Codes', icon: '📋' },
    { id: 'webhook', name: 'Webhook Reference', icon: '🔗' },
    ...apis.map(a => ({ id: a.id, name: a.name, icon: '⚡', endpoint: a.endpoint })),
  ];

  const results = search.trim()
    ? allPages.filter(p =>
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        (p.endpoint || '').toLowerCase().includes(search.toLowerCase())
      )
    : [];

  const handleSelect = (id) => {
    setActivePage(id);
    setSearch('');
    setSearchOpen(false);
    setMobileSearchOpen(false);
  };

  const closeMobileSearch = () => {
    setMobileSearchOpen(false);
    setSearch('');
  };

  const searchProps = {
    search,
    setSearch,
    searchOpen,
    setSearchOpen,
    results,
    onSelect: handleSelect,
    inputRef: desktopSearchRef,
  };

  return (
    <>
      {searchOpen && search && results.length > 0 && !mobileSearchOpen && (
        <div
          onClick={() => { setSearch(''); setSearchOpen(false); }}
          style={{ position: 'fixed', inset: 0, zIndex: 90 }}
          aria-hidden="true"
        />
      )}

      <header className="app-header">
        {onBackToProducts && (
          <button
            type="button"
            className="app-header__back app-header__icon-btn"
            onClick={onBackToProducts}
            title="All products"
            aria-label="Back to all products"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        )}

        <button
          type="button"
          className="app-header__menu app-header__icon-btn"
          onClick={() => setSidebarOpen(o => !o)}
          aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>

        <div className="app-header__brand" onClick={() => setActivePage('home')} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && setActivePage('home')}>
          <img
            src={`${process.env.PUBLIC_URL}${product?.logo || '/logo.png'}`}
            alt={product?.logoAlt || 'Wiyak'}
            className={`app-header__logo-img${product?.logoWide ? ' is-wide' : ''}`}
          />
          {!product?.logoWide && (
            <div className="app-header__logo-text">
              <div className="app-header__logo-sub">{product?.headerLabel || 'API Docs'}</div>
            </div>
          )}
        </div>

        <div className="app-header__divider" aria-hidden="true" />

        <EnvSwitcher />

        <SearchBar {...searchProps} />

        <div className="app-header__spacer" aria-hidden="true" />

        <TokenBadge />

        <div className="app-header__badge-version">v1.0</div>

        <div className="app-header__badge-status">
          <div className="app-header__status-dot" />
          Online
        </div>

        <button
          type="button"
          className="app-header__search-toggle app-header__icon-btn"
          onClick={() => setMobileSearchOpen(true)}
          aria-label="Open search"
        >
          <SearchIcon />
        </button>

        <button
          type="button"
          className="app-header__theme app-header__icon-btn"
          onClick={toggleTheme}
          title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
        >
          {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
        </button>
      </header>

      {mobileSearchOpen && (
        <MobileSearchOverlay
          search={search}
          setSearch={setSearch}
          results={results}
          onSelect={handleSelect}
          onClose={closeMobileSearch}
          inputRef={mobileSearchRef}
        />
      )}
    </>
  );
}
